import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ImageManager } from '@/lib/imageManager';
import { Hammer } from 'lucide-react';
import * as Icons from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const placeholderCategoryImage = '/images/placeholder-category-sketch.jpg';

interface Category {
  id: string;
  name: string;
  description: string;
  image_url: string;
  icon_name?: string;
  coming_soon?: boolean;
  productCount?: number;
}

const Categories = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  const getCategoryImageUrlWithFallback = (category: Category) => {
    if (!category.image_url || category.image_url.trim() === '') {
      return placeholderCategoryImage;
    }
    // If it's already a full URL (uploaded to Supabase Storage), use it directly
    if (category.image_url.startsWith('http')) {
      return category.image_url;
    }
    // Otherwise, treat it as a Supabase Storage path
    return ImageManager.getImageUrl(category.image_url, 'category');
  };

  const handleImageErrorWithFallback = (e: React.SyntheticEvent<HTMLImageElement>) => {
    ImageManager.handleImageError(e, 'category');
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data: categoriesData, error } = await supabase
          .from('categories')
          .select('*')
          .order('coming_soon', { ascending: true, nullsFirst: true })
          .order('name');

        if (error) {
          console.error('Error fetching categories:', error);
          return;
        }

        // Get product counts for each category
        const categoriesWithCounts = await Promise.all(
          (categoriesData || []).map(async (category) => {
            const { data: count } = await supabase
              .rpc('get_product_count_by_category', { category_id_param: category.id });
            return {
              ...category,
              productCount: count || 0,
            };
          })
        );

        setCategories(categoriesWithCounts);
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  if (loading) {
    return (
      <section className="py-16 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 hebrew-heading">קטגוריות המוצרים</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto hebrew-body">
              מגוון רחב של חומרי בנייה איכותיים לכל פרויקט
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-0">
                  <div className="w-full h-48 bg-gray-200 rounded-t-lg"></div>
                  <div className="p-6">
                    <div className="h-6 bg-gray-200 rounded mb-3"></div>
                    <div className="h-4 bg-gray-200 rounded mb-4"></div>
                    <div className="h-4 bg-gray-200 rounded"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 hebrew-heading">קטגוריות המוצרים</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto hebrew-body">
            מגוון רחב של חומרי בנייה איכותיים לכל פרויקט
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {categories.slice(0, 6).map((category) => {
            const getCategoryIcon = (iconName: string) => {
              if (iconName && (Icons as any)[iconName]) {
                return (Icons as any)[iconName];
              }
              return Hammer;
            };

            const IconComponent = getCategoryIcon(category.icon_name || '');
            const isComingSoon = category.coming_soon === true;

            return (
              <a
                key={category.id}
                href={isComingSoon ? undefined : `/category/${encodeURIComponent(category.name)}`}
                onClick={(e) => {
                  if (isComingSoon) {
                    e.preventDefault();
                    return;
                  }
                  if (!e.ctrlKey && !e.metaKey) {
                    e.preventDefault();
                    navigate(`/category/${encodeURIComponent(category.name)}`);
                  }
                }}
                className={`block ${isComingSoon ? 'pointer-events-none' : ''}`}
                target={isComingSoon ? undefined : "_blank"}
                rel={isComingSoon ? undefined : "noopener noreferrer"}
              >
                <Card
                  className={`group transition-all duration-300 ${isComingSoon
                    ? 'opacity-60 cursor-not-allowed'
                    : 'hover:shadow-xl transform hover:-translate-y-2 cursor-pointer'
                    }`}
                >
                  <CardContent className="p-0">
                    <div className="relative">
                      {category.image_url ? (
                        <img
                          src={getCategoryImageUrlWithFallback(category)}
                          alt={category.name}
                          className={`w-full h-48 object-cover rounded-t-lg ${isComingSoon ? 'grayscale' : ''
                            }`}
                          onError={handleImageErrorWithFallback}
                          loading="lazy"
                          sizes="(max-width: 1024px) 100vw, 33vw"
                          srcSet={`${getCategoryImageUrlWithFallback(category)} 800w`}
                        />
                      ) : (
                        <div className={`w-full h-48 bg-gradient-to-br from-primary to-secondary rounded-t-lg flex items-center justify-center ${isComingSoon ? 'grayscale' : ''
                          }`}>
                          <IconComponent className="h-16 w-16 text-white" />
                        </div>
                      )}

                      {isComingSoon ? (
                        <Badge className="absolute top-4 right-4 bg-amber-500 text-white">
                          בקרוב
                        </Badge>
                      ) : (
                        <Badge className="absolute top-4 right-4 bg-lime text-lime-foreground">
                          {category.productCount || 0} מוצרים
                        </Badge>
                      )}
                    </div>

                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-lime/10 rounded-lg flex items-center justify-center">
                          <IconComponent className="h-5 w-5 text-lime" />
                        </div>
                        <h3 className="text-xl font-bold text-foreground">{category.name}</h3>
                      </div>

                      <p className="text-muted-foreground mb-4">{category.description || 'קטגוריה מגוונת עם מוצרים איכותיים'}</p>

                      {isComingSoon ? null : (
                        <div className="flex justify-end items-center">
                          <span className="text-lime hover:text-lime/80 font-medium group-hover:translate-x-1 transition-transform">
                            צפה בקטגוריה ←
                          </span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </a>
            );
          })}
        </div>

        <div className="text-center">
          <Button
            onClick={() => navigate('/products')}
            variant="outline"
            size="lg"
            className="hover:bg-primary hover:text-primary-foreground transition-all duration-300"
          >
            צפה בכל הקטגוריות
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Categories;